import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import { Box, Button, Card, Stack, Table, TableBody, TableHead, TableRow, Typography } from "@mui/material"
import { useState, useEffect, useRef } from "react"
import { Repeat } from "lib/@components/repeat.js"
import { Loader } from "lib/@components/Loader.js"
import { useBoundContext } from "lib/@components/binding/use-bound-context.js"
import { Bound } from "lib/@components/binding/Bound"
import { useSearchParam } from "lib/@hooks/use-search-param.js"
import Iconify from "minimals-template/components/Iconify.js"
import { getTypeReviewRows } from "routes/asset-register/plugins/asset-mapping/ai-type-review/controller/get-type-review-rows.js"
import { TypeReviewRow } from "routes/asset-register/plugins/asset-mapping/ai-type-review/components/type-review-row.js"
import { TypeReviewHeaderCell } from "routes/asset-register/plugins/asset-mapping/ai-type-review/components/type-review-header-cell.js"
import { typeReviewColumns } from "routes/asset-register/plugins/asset-mapping/ai-type-review/components/type-review-columns.js"
import { TypeReviewSearch } from "routes/asset-register/plugins/asset-mapping/ai-type-review/components/type-review-search.js"
import { confirmTypeReview } from "routes/asset-register/plugins/asset-mapping/ai-type-review/controller/confirm-type-review.js"
import { applyMappingSettings } from "routes/asset-register/plugins/asset-mapping/controller/apply-mapping-settings"
import { AiTypeReviewWidgets } from "routes/asset-register/plugins/asset-mapping/ai-type-review/components/ai-type-review-widgets/ai-type-review-widgets.js"
import { setAllTypeReviews } from "routes/asset-register/plugins/asset-mapping/ai-type-review/controller/set-all-type-reviews.js"
import { useReferenceState } from "lib/@hooks/use-reference"
import { defaultNumberOfRecords } from "lib/@components/pagination/default-number-of-records"
import { PaginationRow } from "../../lib/pagination-row"
import { confirm } from "lib/@dialogs/confirm"
import { uniqueId } from "library/database/split-id.mjs"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { getReviewStatusLabel } from "routes/asset-register/plugins/asset-mapping/ai-type-review/review-status.js"
import noop from "lib/noop"

export function TypeReview() {
    const { target, refreshRegister = noop } = useBoundContext()
    const registerId = target?._id ?? target?.id
    const registerKey = registerId ? uniqueId(registerId) : "default"
    const [busy, setBusy] = useState(false)
    const [reviewOverrides, setReviewOverrides] = useState({})
    const hasLoadedReviewRows = useRef(false)
    const lastReviewRows = useRef([])
    const [reviewRows, refreshReviewRows = noop] = getTypeReviewRows.useResults.withRefetch(registerId) ?? []
    const [search] = useSearchParam("search", "")
    const [filter, setFilter] = useSearchParam("filter", "all")
    const sorter = `asset-mapping-types-review-sort-${registerKey}`
    const [sortArr] = useReferenceState(sorter, [], "task-sort")
    const sort = readSort(sortArr)
    const sourceRows = getSourceRows()
    const rows = sourceRows.map(addRegisterId).map(applyReviewOverride).sort(compareRows)
    const filteredRows = sortRows(filterRows(rows, search, filter))
    const [page, setPage] = useState(1)
    const [storedPageSize, setStoredPageSize] = useReferenceState(
        `asset-mapping-types-review-page-size-${registerKey}`,
        defaultNumberOfRecords[1] ?? 10
    )
    const [pageSize, setPageSize] = useState(+storedPageSize || defaultNumberOfRecords[1] || 10)
    const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize))
    const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize)

    useEffect(() => {
        const maxPage = Math.max(1, Math.ceil(filteredRows.length / pageSize))
        if (page > maxPage) setPage(maxPage)
    }, [filteredRows.length, page, pageSize])

    useEffect(syncStoredPageSize, [storedPageSize])
    useEffect(resetReviewOverrides, [target?._id, target?.id])
    useEffect(resetToFirstPage, [registerId, search])

    if (!hasLoadedReviewRows.current) {
        return <Loader inline color="primary" caption="Loading asset types for review..." />
    }

    return (
        <Bound
            reviewRows={rows}
            busy={busy}
            setBusy={setBusy}
            filter={filter}
            setFilter={setFilterAndResetPage}
            sorter={sorter}
            setSkip={resetToFirstPage}
        >
            <Stack spacing={2} sx={{ py: 2, mt: 1 }}>
                <AiTypeReviewWidgets />
            </Stack>
            <Card sx={{ p: 3, overflow: "visible" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h5">Asset Type Suggestions</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            variant="text"
                            color="inherit"
                            startIcon={<Iconify icon="mdi:refresh" />}
                            onClick={resetAll}
                            disabled={busy || rows.length === 0}
                            data-cy="type-review-reset-all"
                        >
                            Reset all
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Iconify icon="mdi:close" />}
                            onClick={rejectAll}
                            disabled={busy || rows.length === 0 || rows.every((r) => r.reviewStatus === "rejected")}
                            data-cy="type-review-reject-all"
                        >
                            Reject All
                        </Button>
                        <Button
                            variant="contained"
                            onClick={mapAssets}
                            disabled={busy}
                            data-cy="type-review-map-assets"
                        >
                            Map Assets
                        </Button>
                    </Stack>
                </Stack>
                <TypeReviewSearch>
                    <PaginationRow
                        compact
                        page={page}
                        pageCount={pageCount}
                        pageSize={pageSize}
                        onChange={setPage}
                        onPageSizeChange={handlePageSizeChange}
                        dataCy="paged-repeat-pagination-top"
                    />
                </TypeReviewSearch>
                <Box
                    sx={{
                        width: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "8px",
                        "& .MuiTableCell-root": {
                            whiteSpace: "nowrap",
                            py: 1.5,
                        },
                        "& .MuiTableCell-head:first-of-type, & .MuiTableCell-head:last-of-type": {
                            borderRadius: 0,
                        },
                        "& .MuiTableRow-root": {
                            "&": {
                                borderBottom: "1px solid",
                                borderBottomColor: "divider",
                            },
                            "&:last-of-type": {
                                borderBottom: "none",
                            },
                        },
                    }}
                    data-cy="ai-type-review-table-container"
                >
                    <Table
                        stickyHeader
                        size="small"
                        data-cy="ai-type-review-table"
                        sx={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", width: "100%" }}
                    >
                        <TableHead>
                            <TableRow>
                                <Repeat list={typeReviewColumns} item={<TypeReviewHeaderCell />} keyFn="field" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <Repeat
                                list={pagedRows}
                                item={<TypeReviewRow onReviewChange={setReviewOverride} />}
                                keyFn="typeKey"
                            />
                        </TableBody>
                    </Table>
                </Box>
                <PaginationRow
                    page={page}
                    pageCount={pageCount}
                    pageSize={pageSize}
                    onChange={setPage}
                    onPageSizeChange={handlePageSizeChange}
                    dataCy="paged-repeat-pagination-bottom"
                />
            </Card>
        </Bound>
    )

    function setFilterAndResetPage(value) {
        setFilter(value)
        setPage(1)
    }

    function addRegisterId(row) {
        return {
            ...row,
            registerId,
        }
    }

    function getSourceRows() {
        if (Array.isArray(reviewRows)) {
            hasLoadedReviewRows.current = true
            lastReviewRows.current = reviewRows
            return reviewRows
        }
        return lastReviewRows.current
    }

    function applyReviewOverride(row) {
        const reviewOverride = reviewOverrides[row.typeKey]
        if (!reviewOverride) return row

        return {
            ...row,
            ...reviewOverride,
        }
    }

    function resetReviewOverrides() {
        setReviewOverrides({})
    }

    function syncStoredPageSize() {
        setPageSize(+storedPageSize || defaultNumberOfRecords[1] || 10)
    }

    function resetToFirstPage() {
        setPage(1)
    }

    function handlePageSizeChange(value) {
        const nextValue = +value || defaultNumberOfRecords[1] || 10
        setPageSize(nextValue)
        setStoredPageSize(nextValue)
        setPage(1)
    }

    function readSort(storedSort) {
        const raw = (Array.isArray(storedSort) ? storedSort[0] : "") || ""
        if (!raw) return null
        return { field: raw.replace(/^-/, ""), descending: raw.startsWith("-") }
    }

    function sortRows(rowsToSort) {
        if (!sort?.field) return rowsToSort
        return [...rowsToSort].sort(compareBySort)
    }

    function compareBySort(a, b) {
        const direction = sort.descending ? -1 : 1
        return direction * compareSortValues(getSortValue(a), getSortValue(b))
    }

    function getSortValue(row) {
        switch (sort.field) {
            case "suggestedTypeName":
                return getDisplayedTypeName(row) ?? ""
            case "suggestedParent":
                return getDisplayedParent(row) ?? ""
            case "count":
                return row.count ?? 0
            case "reviewStatus":
                return getReviewStatusLabel(row)
            default:
                return row[sort.field] ?? ""
        }
    }

    function compareSortValues(a, b) {
        if (typeof a === "number" && typeof b === "number") return a - b
        return String(a).localeCompare(String(b))
    }

    function compareRows(a, b) {
        const aChanged = a.suggestedTypeName !== a.originalAssetType
        const bChanged = b.suggestedTypeName !== b.originalAssetType
        if (aChanged !== bChanged) return aChanged ? -1 : 1
        if ((a.count ?? 0) !== (b.count ?? 0)) return (b.count ?? 0) - (a.count ?? 0)
        return String(a.typeKey).localeCompare(String(b.typeKey))
    }

    function filterRows(rows, search, filter) {
        return rows.filter(matchesSearch).filter(matchesFilter)

        function matchesSearch(row) {
            if (!search) return true
            const normalizedSearch = search.toLowerCase()
            const searchValues = [
                row.originalAssetType,
                row.identifiedAsset,
                getDisplayedTypeName(row),
                getDisplayedParent(row),
            ]

            return searchValues.some(matchesValue)

            function matchesValue(value) {
                return value?.toLowerCase().includes(normalizedSearch)
            }
        }

        function matchesFilter(row) {
            if (filter === "all") return true
            return row.reviewStatus === filter
        }
    }

    function getDisplayedTypeName(row) {
        if (row.reviewStatus === "rejected") return row.suggestedTypeName
        return row.reviewedTypeName ?? row.suggestedTypeName
    }

    function getDisplayedParent(row) {
        if (row.reviewStatus === "rejected") return row.suggestedParent
        return row.reviewedParent ?? row.suggestedParent
    }

    async function rejectAll() {
        if (
            await confirm(
                "This rejects every AI suggestion and reverts all asset types back to their original uploaded type. Any edits or individual decisions you've made will be replaced.",
                "Reject all suggestions?",
                undefined,
                "Reject all"
            )
        ) {
            await runBulkReviewAction(
                "rejected",
                getRejectedTypeName,
                getRejectedParent,
                "Failed to reject all asset type reviews"
            )
        }
    }

    async function resetAll() {
        if (
            await confirm(
                "This discards all your changes — including any rejections and edits — and restores every asset type to its accepted AI suggestion.",
                "Reset all to AI suggestions?",
                undefined,
                "Reset all"
            )
        ) {
            await runBulkReviewAction(
                "approved",
                getResetTypeName,
                getResetParent,
                "Failed to reset all asset type reviews"
            )
        }
    }

    async function mapAssets() {
        setBusy(true)
        try {
            await confirmTypeReview(registerId)
            const started = await applyMappingSettings(registerId)
            if (!started) warningSnackbar("Mapping is already running for this register.")
            refreshRegister()
        } finally {
            setBusy(false)
        }
    }

    async function saveAllRowsReview(reviewStatus, getReviewedTypeName, getReviewedParent) {
        const previousOverrides = reviewOverrides
        applyBulkReviewOverride(reviewStatus, getReviewedTypeName, getReviewedParent)
        setBusy(true)
        try {
            await setAllTypeReviews({
                id: registerId,
                reviewStatus,
            })
            refreshReviewRows()
        } catch (error) {
            setReviewOverrides(previousOverrides)
            throw error
        } finally {
            setBusy(false)
        }
    }

    async function runBulkReviewAction(reviewStatus, getReviewedTypeName, getReviewedParent, failureMessage) {
        try {
            await saveAllRowsReview(reviewStatus, getReviewedTypeName, getReviewedParent)
        } catch (error) {
            errorSnackbar(failureMessage)
        }
    }

    function getResetTypeName(row) {
        return row.suggestedTypeName
    }

    function getResetParent(row) {
        return row.suggestedParent
    }

    function getRejectedTypeName(row) {
        return row.originalAssetType
    }

    function getRejectedParent() {
        return null
    }

    function applyBulkReviewOverride(reviewStatus, getReviewedTypeName, getReviewedParent) {
        const nextOverrides = {}
        for (const row of rows) {
            nextOverrides[row.typeKey] = {
                reviewStatus,
                reviewedTypeName: getReviewedTypeName(row),
                reviewedParent: getReviewedParent(row),
            }
        }
        setReviewOverrides(nextOverrides)
    }

    function setReviewOverride(typeKey, review) {
        setReviewOverrides(updateReviewOverrides)

        function updateReviewOverrides(currentOverrides) {
            return {
                ...currentOverrides,
                [typeKey]: review,
            }
        }
    }
}
